import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { OrphansPanel } from "@/components/OrphansPanel";
import { Pager, PageLayout, PagerBlock } from "@/components/Pager";
import { NarrationReviewPanel } from "@/components/NarrationReviewPanel";
import { ConflictReviewPanel } from "@/components/ConflictReviewPanel";
import { Toast } from "@/components/Toast";
import { WordingSheet } from "@/components/WordingSheet";
import { ReaderSelection } from "@/lib/selection";
import { useReaderSelection } from "@/hooks/useReaderSelection";
import { useBookReader, useChapter, ReaderSegment } from "@/hooks/useReader";
import { useReadingPosition } from "@/hooks/useReadingPosition";
import { clearLastBook, setLastBook } from "@/lib/lastBook";
import { useSyncBook, useSyncStatus } from "@/hooks/useSyncBook";
import {
  useSetAudioRegenPolicy,
  useSynthesizeAudio,
  useTranslateChapter,
  useUpdateSegment,
} from "@/hooks/useTranslate";

// Typography/spacing for the paginator's text column. The Pager owns the
// column width and horizontal insets, so this carries only the font, vertical
// padding, and inter-block spacing (`[&>*]:mb-4` replaces the old flex gap —
// trailing margins are truncated at a column break, so they don't push text).
// `select-none` (+ the iOS callout off) suppresses the browser's native text
// selection so mobile Chrome/Safari don't pop their own selection toolbar and
// contextual-search sheet over ours; useReaderSelection re-implements selection
// on top of this with our own word-snapped highlight.
const CONTENT_CLASS =
  "font-serif text-[1.05rem] py-6 [&>*]:mb-4 select-none [-webkit-touch-callout:none]";

// Segments that can be narrated (have target text), in reading order.
function narratable(list: ReaderSegment[]): ReaderSegment[] {
  return list.filter((s) => (s.targetText ?? "").trim().length > 0);
}

// The segment sitting at the top of the given page: the last one that begins on
// or before it (a page can open on the tail of a paragraph that started
// earlier). pageOfKey is monotonic in reading order, so the scan can stop early.
function topSegmentOnPage(
  segments: ReaderSegment[],
  pageOfKey: Record<string, number>,
  page: number,
): ReaderSegment | null {
  let best: ReaderSegment | null = null;
  for (const s of segments) {
    if ((pageOfKey[s.id] ?? 0) <= page) best = s;
    else break;
  }
  return best ?? segments[0] ?? null;
}

export default function Reader() {
  const { slug = "" } = useParams();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: book, isLoading } = useBookReader(slug);
  const sync = useSyncBook();
  const status = useSyncStatus(slug, !!slug);
  const translateChapter = useTranslateChapter();

  const [chapterIndex, setChapterIndex] = useState(0);
  const [pageIndex, setPageIndex] = useState(0);
  const [direction, setDirection] = useState<"next" | "prev">("next");
  const [layout, setLayout] = useState<PageLayout>({
    pageCount: 1,
    pageOfKey: {},
  });
  // When entering a chapter backwards, jump to its last page once measured.
  const pendingRef = useRef<"last" | null>(null);
  // Restore / reflow anchoring. anchorRef holds the externalId of the page's
  // first visible segment; on every re-measure we snap back to its page so a
  // resize, font change, or live content edit keeps the reader in place.
  const anchorRef = useRef<string | null>(null);
  // One-shot page resolver applied on the next measurement of a freshly-entered
  // chapter (used to restore a page from the URL / saved position).
  const resolvePageRef = useRef<
    ((l: PageLayout, segs: ReaderSegment[]) => number) | null
  >(null);
  const phaseRef = useRef<"init" | "restoring" | "live">("init");
  const [restored, setRestored] = useState(false);
  // Latest layout, readable from the once-only restore effect.
  const layoutRef = useRef(layout);
  layoutRef.current = layout;
  const searchParamsRef = useRef(searchParams);
  searchParamsRef.current = searchParams;
  // Which chapter the current `layout` was measured for (-1 = none yet), so the
  // restore effect knows whether it can place a page now or must defer.
  const chapterIndexRef = useRef(chapterIndex);
  chapterIndexRef.current = chapterIndex;
  const measuredChapterRef = useRef(-1);

  const [navOpen, setNavOpen] = useState(false);
  const [translating, setTranslating] = useState(false);
  const [panelOpen, setPanelOpen] = useState(false);
  const [narrationOpen, setNarrationOpen] = useState(false);
  const [conflictOpen, setConflictOpen] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  // Snapshot of the text selection handed to the rewording sheet (kept stable
  // while the sheet is open). Selecting any reader text opens this sheet
  // directly — there is no intermediate floating menu.
  const [wordingSel, setWordingSel] = useState<ReaderSelection | null>(null);
  // Listen mode: continuous narration playback with a shared <audio> element.
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [listening, setListening] = useState(false);
  const [paused, setPaused] = useState(false);
  const [playingId, setPlayingId] = useState<string | null>(null);
  const [rate, setRate] = useState(1);
  const lastSeenPullSummary = useRef<string | null>(null);
  // undefined = not pushing; string|null = lastSyncedAt snapshot at click time
  const [pushBaseline, setPushBaseline] = useState<string | null | undefined>(
    undefined,
  );
  const [justPushed, setJustPushed] = useState(false);

  useEffect(() => {
    const raw = status.data?.lastPullSummary ?? null;
    if (raw === null || raw === lastSeenPullSummary.current) return;
    if (lastSeenPullSummary.current !== null) {
      let summary: { updated?: number; created?: number; orphaned?: number } =
        {};
      try {
        summary = JSON.parse(raw);
      } catch {
        /* ignore parse error */
      }
      setToast(
        `Pull: ${summary.updated ?? 0} updated, ${summary.created ?? 0} added, ${summary.orphaned ?? 0} orphaned`,
      );
    }
    lastSeenPullSummary.current = raw;
  }, [status.data?.lastPullSummary]);

  useEffect(() => {
    if (pushBaseline === undefined) return;
    const current = status.data?.lastSyncedAt ?? null;
    if (current === pushBaseline) return;
    setPushBaseline(undefined);
    // The sync may have pulled new text into the DB — refetch so the open
    // page reflects it without a manual reload.
    queryClient.invalidateQueries({ queryKey: ["book-reader", slug] });
    queryClient.invalidateQueries({ queryKey: ["chapter"] });
    // The pull ran but the push was blocked by conflicts — surface them
    // instead of a misleading "Synced".
    if (status.data?.conflictCount) {
      setConflictOpen(true);
      setToast(
        `Sync paused: ${status.data.conflictCount} conflict(s) to resolve before pushing`,
      );
      return;
    }
    setJustPushed(true);
    const t = setTimeout(() => setJustPushed(false), 3000);
    return () => clearTimeout(t);
  }, [
    status.data?.lastSyncedAt,
    status.data?.conflictCount,
    pushBaseline,
    queryClient,
    slug,
  ]);

  useEffect(() => {
    if (pushBaseline === undefined) return;
    const t = setTimeout(() => setPushBaseline(undefined), 60_000);
    return () => clearTimeout(t);
  }, [pushBaseline]);

  const chapters = book?.chapters ?? [];
  const current = chapters[chapterIndex];
  // Poll while listening (to pick up freshly-synthesized narration) or while a
  // translation job runs.
  const { data: segments } = useChapter(
    current?.id ?? null,
    translating,
    listening && !paused,
  );
  const updateSegment = useUpdateSegment(current?.id ?? null);
  const synthesizeAudio = useSynthesizeAudio(current?.id ?? null);
  const setRegenPolicy = useSetAudioRegenPolicy(current?.id ?? null);
  const {
    position,
    isFetched: positionFetched,
    save: savePosition,
  } = useReadingPosition(slug, book?.id);

  // Latest values, readable from memoized callbacks/handlers without re-binding.
  const segmentsRef = useRef<ReaderSegment[]>([]);
  segmentsRef.current = segments ?? [];
  const synthRef = useRef(synthesizeAudio);
  synthRef.current = synthesizeAudio;
  const listeningRef = useRef(listening);
  listeningRef.current = listening;
  const pausedRef = useRef(paused);
  pausedRef.current = paused;
  const rateRef = useRef(rate);
  rateRef.current = rate;
  // Latest control fns for the Media Session handlers (installed once per listen
  // session, but must call the current closures). `skip`/`stopListening` are
  // hoisted declarations, so referencing them here is safe.
  const skipRef = useRef<(d: number) => void>(() => {});
  skipRef.current = skip;
  const stopRef = useRef<() => void>(() => {});
  stopRef.current = stopListening;
  // Set when we roll into a new chapter mid-listen; its first narratable
  // paragraph auto-starts once the chapter's segments load.
  const autostartRef = useRef(false);

  const playUrl = useCallback((url: string) => {
    const el = audioRef.current;
    if (!el) return;
    el.pause();
    el.src = url;
    el.playbackRate = rateRef.current;
    void el.play().catch(() => {});
  }, []);

  // Audio we can't play as-is: none yet, or outdated and flagged to refresh
  // before it plays. Either way we (re)synthesize rather than play stale bytes.
  const needsFreshAudio = (s: ReaderSegment) =>
    !s.audioUrl || s.regenerateBeforePlay;

  const prefetchAhead = useCallback((segs: ReaderSegment[], index: number) => {
    for (let i = index + 1; i <= index + 2 && i < segs.length; i++) {
      const s = segs[i];
      if (s && needsFreshAudio(s) && s.audioStatus !== "audio_pending")
        synthRef.current.mutate(s.id);
    }
  }, []);

  // Start/jump narration at a segment id (stable: safe to use inside `blocks`).
  const playFrom = useCallback(
    (segId: string) => {
      const segs = narratable(segmentsRef.current);
      const idx = segs.findIndex((s) => s.id === segId);
      if (idx < 0) return;
      const seg = segs[idx];
      setListening(true);
      setPaused(false);
      setPlayingId(seg.id);
      prefetchAhead(segs, idx);
      // Play synchronously within the click gesture when fresh audio is ready
      // (unlocks audio); otherwise request synthesis — the effect plays it on
      // arrival. Outdated "regenerate before play" audio takes the synth path.
      if (!needsFreshAudio(seg) && audioRef.current) {
        audioRef.current.dataset.segid = seg.id;
        playUrl(seg.audioUrl!);
      } else {
        synthRef.current.mutate(seg.id);
      }
    },
    [playUrl, prefetchAhead],
  );

  // Jump narration to the first narratable paragraph that *begins* on `page`
  // (current chapter). Used when the reader turns the page mid-listen so the
  // audio follows the page rather than droning on from the previous one. Pages
  // that only carry the tail of a paragraph started earlier have no paragraph
  // beginning on them — we leave playback alone there (it's already on screen).
  const playFromPage = useCallback(
    (page: number) => {
      const pageOf = layoutRef.current.pageOfKey;
      const seg = narratable(segmentsRef.current).find(
        (s) => (pageOf[s.id] ?? 0) === page,
      );
      if (seg) playFrom(seg.id);
    },
    [playFrom],
  );

  // Drive playback off the current playingId: play when its audio is ready,
  // request synthesis if missing, and keep prefetching ahead.
  useEffect(() => {
    if (!listening || paused || !playingId) return;
    const segs = narratable(segments ?? []);
    const idx = segs.findIndex((s) => s.id === playingId);
    if (idx < 0) return;
    const seg = segs[idx];
    const el = audioRef.current;
    if (!el) return;
    if (
      !needsFreshAudio(seg) &&
      seg.audioStatus === "audio_ready" &&
      el.dataset.segid !== seg.id
    ) {
      el.dataset.segid = seg.id;
      playUrl(seg.audioUrl!);
      prefetchAhead(segs, idx);
    } else if (
      needsFreshAudio(seg) &&
      seg.audioStatus !== "audio_pending" &&
      // Don't silently re-request synthesis that already failed — that loops
      // every poll with no feedback. The footer surfaces it with Retry/Skip.
      seg.audioStatus !== "audio_failed"
    ) {
      synthRef.current.mutate(seg.id);
    }
  }, [segments, listening, paused, playingId, playUrl, prefetchAhead]);

  // Auto-start the next chapter's narration after crossing a chapter boundary.
  useEffect(() => {
    if (!autostartRef.current || !segments) return;
    autostartRef.current = false;
    const first = narratable(segments)[0];
    if (first) playFrom(first.id);
    else stopListening();
  }, [segments]); // eslint-disable-line react-hooks/exhaustive-deps

  // Highlight the active paragraph without re-rendering the paginated blocks.
  useEffect(() => {
    document
      .querySelectorAll(".tts-playing")
      .forEach((el) => el.classList.remove("tts-playing"));
    if (playingId) {
      document
        .querySelector(`[data-tts-seg="${playingId}"]`)
        ?.classList.add("tts-playing");
    }
  }, [playingId, pageIndex, segments]);

  // Keep the playing paragraph on screen by turning to its page.
  useEffect(() => {
    if (!listening || !playingId || !segments) return;
    if (layout.pageOfKey[playingId] === undefined) return;
    const target = layout.pageOfKey[playingId];
    setPageIndex((p) => (p === target ? p : target));
  }, [playingId, listening, segments, layout]);

  // Media Session: lock-screen / notification transport controls, and a signal
  // to the OS that we're playing media (so mobile keeps the tab alive and the
  // headset/earbud buttons work). Handlers are installed for the whole listen
  // session and delegate to the latest control closures via refs. Feature-
  // detected; a no-op where the API is missing. (Background playback still ends
  // when a clip finishes — the next-clip hand-off needs JS that's throttled
  // while backgrounded — but transport controls and single-clip lock-screen
  // playback now work.)
  const MEDIA_ACTIONS = [
    "play",
    "pause",
    "previoustrack",
    "nexttrack",
    "stop",
  ] as const;
  useEffect(() => {
    if (!("mediaSession" in navigator)) return;
    const ms = navigator.mediaSession;
    const clear = () => {
      for (const a of MEDIA_ACTIONS) {
        try {
          ms.setActionHandler(a, null);
        } catch {
          /* unsupported action on this browser */
        }
      }
    };
    if (!listening) {
      clear();
      return;
    }
    const set = (a: MediaSessionAction, h: MediaSessionActionHandler) => {
      try {
        ms.setActionHandler(a, h);
      } catch {
        /* unsupported action on this browser */
      }
    };
    set("play", () => {
      setPaused(false);
      void audioRef.current?.play().catch(() => {});
    });
    set("pause", () => {
      setPaused(true);
      audioRef.current?.pause();
    });
    set("previoustrack", () => skipRef.current(-1));
    set("nexttrack", () => skipRef.current(1));
    set("stop", () => stopRef.current());
    return clear;
  }, [listening]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reflect play/pause/stopped to the OS so the lock-screen control and our
  // in-app button stay in sync both ways.
  useEffect(() => {
    if (!("mediaSession" in navigator)) return;
    navigator.mediaSession.playbackState = !listening
      ? "none"
      : paused
        ? "paused"
        : "playing";
  }, [listening, paused]);

  // What the lock screen shows. Kept at chapter granularity (not per paragraph)
  // so the notification doesn't churn on every clip.
  useEffect(() => {
    if (!("mediaSession" in navigator)) return;
    if (!listening || !book) {
      navigator.mediaSession.metadata = null;
      return;
    }
    navigator.mediaSession.metadata = new MediaMetadata({
      title: current?.displayTitle || book.title,
      artist: book.title,
      album: book.title,
      artwork: [
        { src: "/icon.svg", sizes: "any", type: "image/svg+xml" },
        { src: "/icon.png", sizes: "512x512", type: "image/png" },
        { src: "/apple-touch-icon.png", sizes: "180x180", type: "image/png" },
      ],
    });
  }, [listening, book, current]);

  function stopListening() {
    setListening(false);
    setPaused(false);
    setPlayingId(null);
    const el = audioRef.current;
    if (el) {
      el.pause();
      el.dataset.segid = "";
    }
  }

  // Advance to the next paragraph (then page, then chapter) when a clip ends.
  function onAudioEnded() {
    if (!listeningRef.current) return;
    const segs = narratable(segmentsRef.current);
    const idx = segs.findIndex((s) => s.id === playingId);
    const next = segs[idx + 1];
    if (next) {
      setPlayingId(next.id);
    } else if (chapterIndex < chapters.length - 1) {
      autostartRef.current = true;
      goToChapter(chapterIndex + 1);
    } else {
      stopListening();
    }
  }

  function startListening() {
    const segs = segmentsRef.current;
    const top = topSegmentOnPage(segs, layoutRef.current.pageOfKey, pageIndex);
    const startBlock = top ? segs.findIndex((s) => s.id === top.id) : 0;
    const fromId =
      narratable(segs.slice(Math.max(startBlock, 0)))[0]?.id ??
      narratable(segs)[0]?.id;
    if (fromId) playFrom(fromId);
  }

  function togglePause() {
    const el = audioRef.current;
    if (!el) return;
    if (paused) {
      setPaused(false);
      void el.play().catch(() => {});
    } else {
      setPaused(true);
      el.pause();
    }
  }

  function skip(delta: number) {
    const segs = narratable(segmentsRef.current);
    const idx = segs.findIndex((s) => s.id === playingId);
    const target = segs[idx + delta];
    if (!target) return;
    if (audioRef.current) audioRef.current.dataset.segid = "";
    setPaused(false);
    setPlayingId(target.id);
  }

  function cycleRate() {
    const steps = [1, 1.25, 1.5, 0.75];
    setRate((r) => {
      const next = steps[(steps.indexOf(r) + 1) % steps.length];
      if (audioRef.current) audioRef.current.playbackRate = next;
      return next;
    });
  }

  // Feed the current clip's progress to the lock-screen scrubber. Each paragraph
  // is its own clip, so the scrubber tracks/resets per paragraph — approximate,
  // but better than an empty bar.
  function updatePositionState() {
    const ms = "mediaSession" in navigator ? navigator.mediaSession : null;
    const el = audioRef.current;
    if (!ms?.setPositionState || !el) return;
    const { duration, currentTime, playbackRate } = el;
    if (!Number.isFinite(duration) || duration <= 0) return;
    try {
      ms.setPositionState({
        duration,
        position: Math.min(Math.max(currentTime, 0), duration),
        playbackRate: playbackRate || 1,
      });
    } catch {
      /* invalid state (e.g. mid-seek) — skip this tick */
    }
  }

  // Remember this as the last-opened book so the app root (`/`) reopens it.
  // If the slug no longer resolves, forget it so root falls back to the library.
  useEffect(() => {
    if (book) setLastBook(slug);
    else if (!isLoading && slug) clearLastBook();
  }, [book, isLoading, slug]);

  // Reset to the first chapter/page when the book changes.
  useEffect(() => {
    setChapterIndex(0);
    setPageIndex(0);
    pendingRef.current = null;
    anchorRef.current = null;
    resolvePageRef.current = null;
    measuredChapterRef.current = -1;
    phaseRef.current = "init";
    setRestored(false);
  }, [slug]);

  // Restore the reading position once, on mount. The URL (?ch=&p=) wins so deep
  // links / bookmarks are honoured; otherwise fall back to the saved DB position
  // (used when opening the book from the library, which carries no params).
  useEffect(() => {
    if (phaseRef.current !== "init" || !chapters.length) return;

    const chParam = Number(searchParamsRef.current.get("ch"));
    const pParam = Number(searchParamsRef.current.get("p"));

    let targetChapter: number;
    let resolvePage: (l: PageLayout, segs: ReaderSegment[]) => number;

    if (Number.isFinite(chParam) && chParam >= 1) {
      targetChapter = Math.min(chParam - 1, chapters.length - 1);
      const wanted = Number.isFinite(pParam) && pParam >= 1 ? pParam - 1 : 0;
      resolvePage = (l) => Math.min(Math.max(wanted, 0), l.pageCount - 1);
    } else {
      // No URL params → use the saved DB position once it has loaded.
      if (!positionFetched) return;
      if (!position) {
        phaseRef.current = "live";
        setRestored(true);
        return;
      }
      anchorRef.current = position.segmentExternalId;
      const ext = position.segmentExternalId;
      targetChapter = Math.max(
        chapters.findIndex((c) => c.id === position.chapterId),
        0,
      );
      resolvePage = (l, segs) => {
        const seg = segs.find((s) => s.externalId === ext);
        return seg ? (l.pageOfKey[seg.id] ?? 0) : 0;
      };
    }

    phaseRef.current = "restoring";
    if (targetChapter !== chapterIndex) {
      // Chapter will switch → apply on its first measurement (handleLayout).
      resolvePageRef.current = resolvePage;
      setChapterIndex(targetChapter);
    } else if (measuredChapterRef.current === targetChapter) {
      // Same chapter, already measured → place the page now.
      setPageIndex(resolvePage(layoutRef.current, segmentsRef.current));
      phaseRef.current = "live";
      setRestored(true);
    } else {
      // Same chapter, not measured yet → apply on the first measurement.
      resolvePageRef.current = resolvePage;
    }
  }, [positionFetched, position, chapters]);

  const goToChapter = (next: number, page: "first" | "last" = "first") => {
    setDirection(
      next > chapterIndex ? "next" : next < chapterIndex ? "prev" : direction,
    );
    pendingRef.current = page === "last" ? "last" : null;
    setChapterIndex(next);
    setPageIndex(0);
  };

  const nextPage = useCallback(() => {
    setDirection("next");
    if (pageIndex < layout.pageCount - 1) {
      const np = pageIndex + 1;
      setPageIndex(np);
      // Mid-listen, follow the page: narrate from the top of the page we land
      // on instead of carrying on from the paragraph we left behind.
      if (listeningRef.current) playFromPage(np);
    } else if (chapterIndex < chapters.length - 1) {
      pendingRef.current = null;
      if (listeningRef.current) autostartRef.current = true;
      setChapterIndex((c) => c + 1);
      setPageIndex(0);
    }
  }, [
    pageIndex,
    layout.pageCount,
    chapterIndex,
    chapters.length,
    playFromPage,
  ]);

  const prevPage = useCallback(() => {
    setDirection("prev");
    if (pageIndex > 0) {
      const pp = pageIndex - 1;
      setPageIndex(pp);
      if (listeningRef.current) playFromPage(pp);
    } else if (chapterIndex > 0) {
      // Crossing back a chapter mid-listen: jump to its first page and narrate
      // from the top (we'd otherwise need the new layout to find its last page).
      if (listeningRef.current) {
        autostartRef.current = true;
        setChapterIndex((c) => c - 1);
        setPageIndex(0);
      } else {
        pendingRef.current = "last";
        setChapterIndex((c) => c - 1);
      }
    }
  }, [pageIndex, chapterIndex, playFromPage]);

  const handleLayout = useCallback((l: PageLayout) => {
    setLayout(l);
    measuredChapterRef.current = chapterIndexRef.current;

    let target: number | null = null;
    if (resolvePageRef.current) {
      // Restoring a freshly-entered chapter's page (from URL / saved position).
      target = resolvePageRef.current(l, segmentsRef.current);
      resolvePageRef.current = null;
    } else if (pendingRef.current === "last") {
      pendingRef.current = null;
      target = l.pageCount - 1;
    } else if (anchorRef.current) {
      // Reflow (resize / font / live edit) → keep the anchored segment on screen.
      const seg = segmentsRef.current.find(
        (s) => s.externalId === anchorRef.current,
      );
      if (seg) target = l.pageOfKey[seg.id] ?? null;
    }

    // First measurement after restore begins → we're now live.
    if (phaseRef.current === "restoring") {
      phaseRef.current = "live";
      setRestored(true);
    }

    setPageIndex((p) => target ?? Math.min(p, l.pageCount - 1));
  }, []);

  // Mirror the current chapter/page into the URL (?ch=&p=, 1-based) once live,
  // using replace so page turns don't flood browser history.
  useEffect(() => {
    if (!restored) return;
    const ch = String(chapterIndex + 1);
    const p = String(pageIndex + 1);
    const cur = searchParamsRef.current;
    if (cur.get("ch") === ch && cur.get("p") === p) return;
    const next = new URLSearchParams(cur);
    next.set("ch", ch);
    next.set("p", p);
    setSearchParams(next, { replace: true });
  }, [restored, chapterIndex, pageIndex, setSearchParams]);

  // Persist the current page's first segment (debounced) once we're live.
  useEffect(() => {
    if (!restored || !segments?.length) return;
    const ext = topSegmentOnPage(
      segments,
      layout.pageOfKey,
      pageIndex,
    )?.externalId;
    if (!ext) return;
    anchorRef.current = ext;
    savePosition(ext);
  }, [restored, pageIndex, chapterIndex, layout, segments, savePosition]);

  // Keyboard: ←/→ turn pages (crossing chapters), Shift+←/→ jump chapters.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      if (
        t &&
        (t.tagName === "INPUT" ||
          t.tagName === "TEXTAREA" ||
          t.isContentEditable)
      )
        return;
      if (e.key === "ArrowLeft") {
        e.shiftKey ? goToChapter(Math.max(0, chapterIndex - 1)) : prevPage();
      } else if (e.key === "ArrowRight") {
        e.shiftKey
          ? goToChapter(Math.min(chapters.length - 1, chapterIndex + 1))
          : nextPage();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [nextPage, prevPage, chapterIndex, chapters.length]);

  // Selecting reader text (tap a word, or long-press then drag to extend) opens
  // the rewording sheet. Native selection is suppressed (see CONTENT_CLASS), so
  // this hook owns selection: it paints our own highlight and hands back the
  // chosen phrase. Disabled while the sheet is open. isSelecting() lets the
  // Pager/onClick ignore the gesture that produced the selection.
  const { clear: clearSelection, isSelecting } = useReaderSelection({
    enabled: !wordingSel,
    // Tap jumps narration only while it's actually playing; pause flips tapping
    // back to word-selection so you can inspect/reword without leaving listen
    // mode. Long-press always selects.
    isPlaying: () => listeningRef.current && !pausedRef.current,
    onSelect: setWordingSel,
    onTapWhilePlaying: playFrom,
  });

  // A page turn / chapter change re-lays-out the text — close any open sheet
  // and drop the highlight.
  useEffect(() => {
    setWordingSel(null);
    clearSelection();
  }, [pageIndex, chapterIndex, clearSelection]);

  // Stop polling once everything in the chapter is translated.
  useEffect(() => {
    if (translating && segments && segments.every((s) => s.translated))
      setTranslating(false);
  }, [translating, segments]);

  // Memoized so the array identity is stable across renders — the Pager keys its
  // measurement effect on `blocks`, and a fresh array each render would loop.
  // MUST stay above the early returns so hook order is stable.
  const blocks: PagerBlock[] = useMemo(
    () =>
      (segments ?? []).map((seg) =>
        seg.blockType === "heading"
          ? {
              key: seg.id,
              node: (
                <h2 className="font-sans text-xl font-semibold text-slate-900">
                  {seg.targetText?.trim() || seg.sourceText}
                </h2>
              ),
            }
          : {
              key: seg.id,
              node: (
                // Tap-to-jump (while listening) and text selection are both
                // owned by useReaderSelection, which hit-tests touch/mouse
                // points — so this paragraph carries no click handler.
                <p
                  data-tts-seg={seg.id}
                  className={`leading-relaxed ${
                    seg.translated ? "text-slate-900" : "italic text-slate-500"
                  }`}
                >
                  {seg.targetText?.trim() || seg.sourceText || ""}
                </p>
              ),
            },
      ),
    [segments],
  );

  if (isLoading)
    return (
      <div className="grid min-h-screen place-items-center text-slate-400">
        Loading…
      </div>
    );
  if (!book)
    return (
      <div className="grid min-h-screen place-items-center gap-3 text-slate-400">
        <span>Book not found.</span>
        <Link
          to="/library"
          className="text-sm text-slate-500 hover:text-slate-800"
        >
          ← Back to library
        </Link>
      </div>
    );

  const untranslated = (segments ?? []).some((s) => !s.translated);

  const onTranslate = async () => {
    if (!current) return;
    await translateChapter.mutateAsync(current.id);
    setTranslating(true);
  };

  // Share the selected quote. The native share sheet maps title → note title
  // and text → note body (so Google Keep shows "Quote from …" as the heading);
  // where it's unavailable we copy the quote and open Keep for a manual paste.
  const shareSelection = async (sel: ReaderSelection) => {
    const payload = { title: `Quote from “${book.title}”`, text: sel.text };
    if (navigator.share) {
      try {
        await navigator.share(payload);
      } catch {
        /* user dismissed the share sheet */
      }
      return;
    }
    try {
      await navigator.clipboard.writeText(sel.text);
    } catch {
      /* clipboard unavailable */
    }
    window.open("https://keep.google.com/", "_blank", "noopener");
    setToast("Quote copied — paste it into Keep");
  };

  const atFirstPage = chapterIndex === 0 && pageIndex === 0;
  const atLastPage =
    chapterIndex >= chapters.length - 1 && pageIndex >= layout.pageCount - 1;

  const narr = narratable(segments ?? []);
  const playingPos = narr.findIndex((s) => s.id === playingId);
  const playingSeg = playingPos >= 0 ? narr[playingPos] : null;
  // Narration of the current paragraph hasn't arrived yet: either being
  // synthesized (show a spinner) or it failed (offer Retry/Skip). Without this
  // the player just goes quiet between clips with no explanation.
  const audioFailed = playingSeg?.audioStatus === "audio_failed";
  const synthesizing =
    !!playingSeg && needsFreshAudio(playingSeg) && !audioFailed;
  const listenLabel =
    playingPos >= 0 ? `¶ ${playingPos + 1}/${narr.length}` : "…";

  function retryAudio() {
    if (playingId) synthRef.current.mutate(playingId);
  }

  return (
    <div className="flex h-screen flex-col bg-white">
      <header className="flex items-center gap-2 border-b border-slate-200 bg-white/90 px-4 py-3 backdrop-blur">
        <Link to="/library" className="text-slate-500 hover:text-slate-800">
          ←
        </Link>
        <button
          onClick={() => setNavOpen((v) => !v)}
          className="flex-1 truncate text-left"
        >
          <span className="text-sm font-medium text-slate-900">
            {book.title}
          </span>
          <span className="ml-2 text-xs text-slate-400">
            {current?.displayTitle}
          </span>
        </button>
        {untranslated && (
          <button
            onClick={onTranslate}
            disabled={translating || translateChapter.isPending}
            className="rounded-lg bg-slate-900 px-2.5 py-1 text-xs font-medium text-white hover:bg-slate-800 disabled:opacity-50"
          >
            {translating ? "Translating…" : "Translate"}
          </button>
        )}
        {status.data?.audioReviewCount ? (
          <button
            onClick={() => setNarrationOpen(true)}
            title={`${status.data.audioReviewCount} passage(s) whose narration is outdated — click to review`}
            className="inline-flex items-center gap-1 rounded-lg bg-amber-50 px-2 py-1 text-xs font-medium text-amber-700 hover:bg-amber-100"
          >
            🔊
            <span className="inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-amber-500 px-1 text-[10px] font-bold text-white">
              {status.data.audioReviewCount}
            </span>
          </button>
        ) : null}
        {status.data?.conflictCount ? (
          <button
            onClick={() => setConflictOpen(true)}
            title={`${status.data.conflictCount} sync conflict(s) — syncing is paused until you resolve them`}
            className="inline-flex items-center gap-1 rounded-lg bg-red-50 px-2 py-1 text-xs font-medium text-red-700 hover:bg-red-100"
          >
            ⚠️
            <span className="inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-red-600 px-1 text-[10px] font-bold text-white">
              {status.data.conflictCount}
            </span>
          </button>
        ) : null}
        <Link
          to={`/books/${slug}/settings`}
          title="Translation settings"
          className="rounded-lg px-2 py-1 text-xs text-slate-500 hover:bg-slate-100"
        >
          ⚙
        </Link>
        {(() => {
          const pushing = sync.isPending || pushBaseline !== undefined;
          const label = pushing ? "Syncing…" : justPushed ? "Synced" : "Sync";
          const title = pushing
            ? "Syncing with GitHub…"
            : justPushed
              ? "Synced with GitHub"
              : "Sync with GitHub (pull then push)";
          return (
            <button
              onClick={() => {
                setPushBaseline(status.data?.lastSyncedAt ?? null);
                sync.mutate(book.id, {
                  onError: () => setPushBaseline(undefined),
                });
              }}
              disabled={pushing}
              title={title}
              className="relative inline-flex items-center gap-1 rounded-lg border border-slate-200 px-2 py-1 text-xs font-medium text-slate-700 hover:bg-slate-100 disabled:opacity-50"
            >
              {pushing ? (
                <svg
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                  className="h-3.5 w-3.5 animate-spin"
                >
                  <circle
                    cx="12"
                    cy="12"
                    r="9"
                    fill="none"
                    stroke="currentColor"
                    strokeOpacity="0.25"
                    strokeWidth="3"
                  />
                  <path
                    d="M21 12a9 9 0 0 0-9-9"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="3"
                    strokeLinecap="round"
                  />
                </svg>
              ) : (
                <svg
                  viewBox="0 0 16 16"
                  aria-hidden="true"
                  className="h-3.5 w-3.5 fill-current"
                >
                  <path d="M8 0C3.58 0 0 3.58 0 8a8 8 0 0 0 5.47 7.59c.4.07.55-.17.55-.38v-1.33c-2.23.48-2.7-1.07-2.7-1.07-.36-.92-.89-1.17-.89-1.17-.73-.5.05-.49.05-.49.8.06 1.22.82 1.22.82.72 1.23 1.88.88 2.34.67.07-.52.28-.88.5-1.08-1.78-.2-3.64-.89-3.64-3.96 0-.87.31-1.59.82-2.15-.08-.2-.36-1.02.08-2.12 0 0 .67-.21 2.2.82a7.6 7.6 0 0 1 4 0c1.52-1.03 2.2-.82 2.2-.82.44 1.1.16 1.92.08 2.12.51.56.82 1.28.82 2.15 0 3.08-1.87 3.75-3.65 3.95.29.25.54.73.54 1.48v2.2c0 .21.15.46.55.38A8 8 0 0 0 16 8c0-4.42-3.58-8-8-8Z" />
                </svg>
              )}
              <span>{label}</span>
              {status.data?.orphanedCount ? (
                <span
                  role="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setPanelOpen(true);
                  }}
                  className="ml-1 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-red-600 px-1 text-[10px] font-bold text-white"
                  title={`${status.data.orphanedCount} orphaned paragraph(s) — click to review`}
                >
                  {status.data.orphanedCount}
                </span>
              ) : null}
            </button>
          );
        })()}
      </header>

      {navOpen && (
        <nav className="max-h-72 overflow-y-auto border-b border-slate-200 bg-slate-50">
          {chapters.map((ch, i) => (
            <button
              key={ch.id}
              onClick={() => {
                goToChapter(i);
                setNavOpen(false);
              }}
              className={`block w-full px-4 py-2 text-left text-sm ${
                i === chapterIndex
                  ? "bg-slate-200 font-medium"
                  : "hover:bg-slate-100"
              }`}
            >
              {ch.displayTitle}
            </button>
          ))}
        </nav>
      )}

      {!segments ? (
        <div className="flex flex-1 items-center justify-center text-slate-400">
          Loading chapter…
        </div>
      ) : (
        <Pager
          key={current?.id}
          blocks={blocks}
          page={pageIndex}
          onLayout={handleLayout}
          contentClassName={CONTENT_CLASS}
          onSwipeLeft={nextPage}
          onSwipeRight={prevPage}
          suppressSwipe={isSelecting}
        />
      )}

      <footer className="flex flex-col gap-1.5 border-t border-slate-200 bg-white px-4 py-3">
        {listening && (
          <div className="text-center text-[11px] text-slate-400">
            {paused
              ? "Paused · tap a word to select & reword it"
              : "Tap a line to jump here · pause to select text"}
          </div>
        )}
        <div className="flex items-center justify-between gap-2">
          {listening ? (
            <>
              <button
                onClick={stopListening}
                title="Stop listening"
                className="rounded-lg px-2 py-1.5 text-sm text-slate-500 hover:bg-slate-100"
              >
                ✕
              </button>
              <button
                onClick={() => skip(-1)}
                title="Previous paragraph"
                className="rounded-lg px-2 py-1.5 text-slate-600 hover:bg-slate-100"
              >
                ⏮
              </button>
              <button
                onClick={togglePause}
                className="rounded-lg bg-slate-900 px-4 py-1.5 text-sm font-medium text-white hover:bg-slate-800"
              >
                {paused ? "▶ Play" : "⏸ Pause"}
              </button>
              <button
                onClick={() => skip(1)}
                title="Next paragraph"
                className="rounded-lg px-2 py-1.5 text-slate-600 hover:bg-slate-100"
              >
                ⏭
              </button>
              <button
                onClick={cycleRate}
                title="Playback speed"
                className="rounded-lg px-2 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-100"
              >
                {rate}×
              </button>
              {audioFailed ? (
                <span className="ml-auto flex items-center gap-2 text-xs text-rose-600">
                  <span title="Narration couldn't be generated">
                    ⚠ Audio failed
                  </span>
                  <button
                    onClick={retryAudio}
                    className="rounded-lg border border-rose-200 px-2 py-0.5 font-medium text-rose-700 hover:bg-rose-50"
                  >
                    Retry
                  </button>
                  <button
                    onClick={() => skip(1)}
                    className="rounded-lg px-2 py-0.5 font-medium text-slate-600 hover:bg-slate-100"
                  >
                    Skip
                  </button>
                </span>
              ) : synthesizing ? (
                <span className="ml-auto flex items-center gap-1.5 text-xs text-slate-400">
                  <span className="inline-block h-3 w-3 animate-spin rounded-full border-[1.5px] border-slate-300 border-t-slate-600" />
                  Synthesizing… {listenLabel}
                </span>
              ) : (
                <span className="ml-auto text-xs text-slate-400">
                  {listenLabel}
                </span>
              )}
            </>
          ) : (
            <>
              <button
                onClick={prevPage}
                disabled={atFirstPage}
                className="rounded-lg px-3 py-1.5 text-sm text-slate-600 disabled:opacity-30"
              >
                ← Prev
              </button>
              <div className="flex items-center gap-3">
                <span className="text-xs text-slate-400">
                  {current?.displayTitle
                    ? `${chapterIndex + 1}/${chapters.length} · `
                    : ""}
                  p.{pageIndex + 1}/{layout.pageCount}
                </span>
                <button
                  onClick={startListening}
                  title="Listen to this page"
                  className="rounded-lg border border-slate-200 px-2.5 py-1 text-xs font-medium text-slate-700 hover:bg-slate-100"
                >
                  ▶ Listen
                </button>
              </div>
              <button
                onClick={nextPage}
                disabled={atLastPage}
                className="rounded-lg px-3 py-1.5 text-sm text-slate-600 disabled:opacity-30"
              >
                Next →
              </button>
            </>
          )}
        </div>
      </footer>
      <audio
        ref={audioRef}
        onEnded={onAudioEnded}
        onLoadedMetadata={updatePositionState}
        onTimeUpdate={updatePositionState}
        className="hidden"
      />

      <WordingSheet
        selection={wordingSel}
        sourceText={
          wordingSel
            ? ((segments ?? []).find((s) => s.id === wordingSel.segmentId)
                ?.sourceText ?? null)
            : null
        }
        sentenceMap={
          wordingSel
            ? ((segments ?? []).find((s) => s.id === wordingSel.segmentId)
                ?.sentenceMap ?? null)
            : null
        }
        sourceLanguage={book.sourceLanguage}
        targetLanguage={book.targetLanguage}
        saving={updateSegment.isPending}
        hasAudio={
          !!wordingSel &&
          (segments ?? []).find((x) => x.id === wordingSel.segmentId)
            ?.audioStatus === "audio_ready"
        }
        onApply={async (segmentId, fullText, audioPolicy) => {
          setWordingSel(null);
          clearSelection();
          // Save the new wording first; the audio policy is recorded against the
          // updated text (its hash), so it must land after the edit commits.
          await updateSegment.mutateAsync({ segmentId, targetText: fullText });
          if (audioPolicy)
            setRegenPolicy.mutate({ segmentId, policy: audioPolicy });
        }}
        onShare={() => wordingSel && shareSelection(wordingSel)}
        onClose={() => {
          setWordingSel(null);
          clearSelection();
        }}
      />
      <OrphansPanel
        slug={slug}
        open={panelOpen}
        onClose={() => setPanelOpen(false)}
      />
      <NarrationReviewPanel
        slug={slug}
        open={narrationOpen}
        onClose={() => setNarrationOpen(false)}
      />
      <ConflictReviewPanel
        slug={slug}
        open={conflictOpen}
        onClose={() => setConflictOpen(false)}
      />
      {toast && <Toast message={toast} onClose={() => setToast(null)} />}
    </div>
  );
}
